import {
  Box,
  Typography,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useGetFieldDetailsQuery } from '../../../services/mVPGraphQlService';
import {
  AllowedDayType,
  MVPFiltersProps,
} from '../constants';
import { skipToken } from '@reduxjs/toolkit/query';
import { MapSection } from '../timeSeriesTiles/mapContainer';
import { useFilterContext } from '../../../services/mVPMapService/filterContext';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { useState } from 'react';
import AssistantDirectionIcon from '@mui/icons-material/AssistantDirection';
import React from 'react';

const TIME_RANGE = {
  START_TIME: 1717200000,
  END_TIME: Math.floor(new Date().getTime() / 1000),
};

const SECONDS_IN_A_DAY = 24 * 60 * 60;

const FieldDetails: React.FC<MVPFiltersProps> = ({
  dataa,
}) => {
  const { filterValues } = useFilterContext();
  const wsId = useSelector((state: RootState) => state.country.wsId);
  const [centerValue, setCenterValue] = useState();
  const [openDetails, setOpenDetails] = useState(true);
  const [openMap, setOpenMap] = useState(true);

  const selectedFarm = dataa?.fieldNumber;
  const isMobileOrTablet = useMediaQuery(
    '(max-width:1024px)'
  );
  const storedIndex = localStorage?.getItem('filterIndex');

  const storedIndexData = JSON?.parse(storedIndex);

  const updatedFilterValues = {
    ...filterValues,
    index: filterValues?.index || 'NDVI',
  };

  const { data } = useGetFieldDetailsQuery(
    selectedFarm
      ? {
          startTime: TIME_RANGE.START_TIME,
          endTime: TIME_RANGE.END_TIME,
          commonFilters: updatedFilterValues,
          wsId,
          fieldNumbers: [selectedFarm],
        }
      : skipToken
  );

  const fieldData =
    data?.data?.GetFieldDetailsForFieldNumbers
      ?.fieldDetails?.[0];
  const sowingTimeInSeconds =
    fieldData?.fieldDetails?.sowingTime?.seconds;

  const allowedDays: AllowedDayType[] =
    fieldData?.fieldIndexList
      ?.map((ele) => {
        const indexTimeInSeconds = ele.indexTime?.seconds;
        if (indexTimeInSeconds && sowingTimeInSeconds) {
          const day = Math.floor(
            (indexTimeInSeconds - sowingTimeInSeconds) /
              SECONDS_IN_A_DAY
          );
          return { day, indexTime: indexTimeInSeconds };
        }
        return null;
      })
      .filter(
        (value): value is AllowedDayType =>
          value !== null && value?.day >= 0
      ) || [];

  return (
    <Box width="100%">
      {isMobileOrTablet ? (
        <>
          <Accordion
            expanded={openDetails}
            onChange={() => setOpenDetails(!openDetails)}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}>
              <Box
                display="flex"
                flexDirection="column"
                gap="10px">
                {/* Title: Field Details */}
                <Typography
                  sx={{ fontWeight: 'bold', fontSize: 14 }}>
                  Field Details
                </Typography>

                {/* Field No. */}
                {dataa?.fieldNumber && (
                  <Box
                    display="flex"
                    gap="40px"
                    alignItems="center">
                    <Typography
                      sx={{
                        minWidth: '90px',
                        color: 'rgba(0, 0, 0, 0.7)',
                        fontSize: 13,
                      }}>
                      {'Field no.'}
                    </Typography>
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${centerValue?.lat},${centerValue?.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontWeight: 'bold',
                        fontSize: 13,
                        color: 'rgba(0, 143, 237, 1)',
                        textDecoration: 'underline',
                      }}>
                      {dataa?.fieldNumber}
                      <AssistantDirectionIcon
                        sx={{
                          color: 'rgba(0, 143, 237, 1)',
                          marginLeft: '5px',
                        }}
                      />
                    </a>
                  </Box>
                )}

                {dataa?.fieldDetails?.geographyData
                  ?.region && (
                  <Box
                    display="flex"
                    gap="40px"
                    alignItems="center">
                    <Typography
                      sx={{
                        minWidth: '90px',
                        color: 'rgba(0, 0, 0, 0.7)',
                        fontSize: 13,
                      }}>
                      {'Region'}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 'bold',
                        fontSize: 13,
                      }}>
                      {
                        dataa?.fieldDetails?.geographyData
                          ?.region
                      }
                    </Typography>
                  </Box>
                )}
              </Box>
            </AccordionSummary>

            <AccordionDetails>
              <Box display="flex" flexDirection="column">
                {[
                  [
                    'Village',
                    dataa?.fieldDetails?.geographyData
                      ?.village,
                  ],
                  [
                    'Regional Manager',
                    dataa?.fieldDetails?.employeeData
                      ?.regionManager,
                  ],
                  [
                    'FA',
                    dataa?.fieldDetails?.employeeData?.fa,
                  ],
                ].map(([label, value]) => (
                  <Box
                    key={label}
                    display="flex"
                    gap="10px"
                    mb={1.5}>
                    <Typography
                      sx={{
                        minWidth: '120px',
                        color: 'rgba(0, 0, 0, 0.7)',
                        fontSize: 13,
                      }}>
                      {label}
                    </Typography>

                    <Typography
                      sx={{
                        fontWeight: 'bold',
                        fontSize: 13,
                      }}>
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={openMap}
            onChange={() => setOpenMap(!openMap)}
            sx={{ marginTop: 2 }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}>
              <Typography
                sx={{ fontWeight: 'bold', fontSize: 14 }}>
                Time series tile
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box flex={2} marginBottom={5}>
                <MapSection
                  commonFilters={
                    storedIndex
                      ? storedIndexData
                      : updatedFilterValues
                  }
                  fieldNumber={selectedFarm}
                  allowedDays={allowedDays}
                  fromReport={true}
                  setCenterValue={setCenterValue}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </>
      ) : (
        <Box display="flex" flexDirection="row" gap="20px">
          <Box
            flex={1}
            display="flex"
            flexDirection="column"
            padding={2}>
            {[
              ['Field no.', dataa?.fieldNumber],
              [
                'Region',
                dataa?.fieldDetails?.geographyData?.region,
              ],
              [
                'Village',
                dataa?.fieldDetails?.geographyData?.village,
              ],
              [
                'Regional Manager',
                dataa?.fieldDetails?.employeeData
                  ?.regionManager,
              ],
              ['FA', dataa?.fieldDetails?.employeeData?.fa],
            ].map(([label, value]) => (
              <Box
                key={label}
                display="flex"
                gap="10px"
                mb={1.5}>
                <Typography
                  sx={{
                    minWidth: '120px',
                    color: 'rgba(0, 0, 0, 0.7)',
                    fontSize: 13,
                  }}>
                  {label}
                </Typography>
                <Typography
                  sx={{ fontWeight: 'bold', fontSize: 13 }}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box flex={2} padding={2}>
            <MapSection
              commonFilters={
                storedIndex
                  ? storedIndexData
                  : updatedFilterValues
              }
              fieldNumber={selectedFarm}
              allowedDays={allowedDays}
              fromReport={true}
              setCenterValue={setCenterValue}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default FieldDetails;
